import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCityCoordinates } from '../utils/geocodingService';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import { zoomForRadiusMiles } from '../utils/googleMapLayout';

const MAP_HEIGHT = 420;

const STAYS_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
];

const milesBetween = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const boundsFromCenterAndRadius = (center, radiusMiles) => {
  const latDelta = radiusMiles / 69;
  const lngDelta = radiusMiles / (69 * Math.cos((center.lat * Math.PI) / 180));
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
};

const formatPriceLabel = (price) => {
  if (!price) {
    return '—';
  }
  const text = String(price);
  const match = text.match(/\$[\d,]+(?:\.\d{2})?/);
  return match ? match[0] : text;
};

const boundsFromGoogleMap = (map) => {
  const mapBounds = map.getBounds();
  if (!mapBounds) {
    return null;
  }
  const northEast = mapBounds.getNorthEast();
  const southWest = mapBounds.getSouthWest();
  return {
    north: northEast.lat(),
    south: southWest.lat(),
    east: northEast.lng(),
    west: southWest.lng(),
  };
};

const isListingInMapBounds = (listing, bounds) => {
  if (!bounds) {
    return true;
  }
  const lat = Number(listing.latitude);
  const lng = Number(listing.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
};

const createPriceOverlayClass = (google) => {
  class PriceMarkerOverlay extends google.maps.OverlayView {
    constructor({ position, label, listing, isRental, onSelect }) {
      super();
      this.position = position;
      this.label = label;
      this.listing = listing;
      this.isRental = isRental;
      this.onSelect = onSelect;
      this.div = null;
    }

    onAdd() {
      const div = document.createElement('div');
      div.style.cssText = [
        'position:absolute',
        'padding:6px 10px',
        'border-radius:20px',
        'background:#fff',
        'border:2px solid #1a1a1a',
        'box-shadow:0 2px 6px rgba(0,0,0,0.18)',
        'font-size:13px',
        'font-weight:700',
        'color:#1a1a1a',
        'cursor:pointer',
        'white-space:nowrap',
        'user-select:none',
        'transform:translate(-50%,-100%)',
        'z-index:1',
      ].join(';');

      if (this.isRental) {
        div.style.borderColor = '#FF5A5F';
      } else {
        div.style.borderColor = '#1976d2';
      }

      div.textContent = this.label;
      div.addEventListener('click', (event) => {
        event.stopPropagation();
        this.onSelect?.(this.listing);
      });

      this.div = div;
      this.getPanes().overlayMouseTarget.appendChild(div);
    }

    draw() {
      if (!this.div) {
        return;
      }
      const projection = this.getProjection();
      const point = projection?.fromLatLngToDivPixel(this.position);
      if (!point) {
        return;
      }
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y - 8}px`;
    }

    onRemove() {
      if (this.div?.parentNode) {
        this.div.parentNode.removeChild(this.div);
      }
      this.div = null;
    }
  }

  return PriceMarkerOverlay;
};

const SEARCH_AREA_DEBOUNCE_MS = 1100;

function StaysMapView({
  listings,
  initialDestination,
  initialRadiusMiles,
  searchKey,
  onViewportChange,
  onSearchArea,
  onListingSelect,
}) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef(new Map());
  const circleRef = useRef(null);
  const infoWindowRef = useRef(null);
  const userInteractedRef = useRef(false);
  const initialPositionDoneRef = useRef(false);
  const isApplyingSearchViewportRef = useRef(false);
  const onViewportChangeRef = useRef(onViewportChange);
  const mapListenersRef = useRef([]);
  const searchAreaTimerRef = useRef(null);
  const triggerSearchAreaRef = useRef(null);
  const lastSearchKeyRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [mapInstanceReady, setMapInstanceReady] = useState(false);

  const mappableListings = useMemo(
    () =>
      listings.filter(
        (listing) =>
          !listing.isDeepLink &&
          Number.isFinite(Number(listing.latitude)) &&
          Number.isFinite(Number(listing.longitude))
      ),
    [listings]
  );

  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then(() => {
        if (!cancelled) {
          setMapReady(true);
          setMapError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMapError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const removeCircle = useCallback(() => {
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
  }, []);

  const clearMapListeners = useCallback(() => {
    mapListenersRef.current.forEach((listener) => {
      window.google?.maps?.event?.removeListener(listener);
    });
    mapListenersRef.current = [];
  }, []);

  const emitViewport = useCallback((bounds) => {
    if (bounds) {
      onViewportChangeRef.current?.(bounds);
    }
  }, []);

  const showListingInfo = useCallback((map, listing) => {
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    const price = listing.price || t('stays.seePrice');
    const distance = listing.distanceFromCenter ? `<div style="color:#666;font-size:12px">${listing.distanceFromCenter}</div>` : '';
    const content = `
      <div style="max-width:220px;font-family:Roboto,sans-serif">
        <div style="font-weight:600;margin-bottom:4px">${listing.name}</div>
        ${distance}
        <div style="font-weight:700;color:#1976d2;margin:6px 0">${price}</div>
        <a href="${listing.url}" target="_blank" rel="noopener noreferrer" style="color:#1976d2;font-size:13px">
          ${t('stays.bookNow')}
        </a>
      </div>
    `;

    infoWindowRef.current.setContent(content);
    infoWindowRef.current.setPosition({
      lat: Number(listing.latitude),
      lng: Number(listing.longitude),
    });
    infoWindowRef.current.open(map);
    onListingSelect?.(listing);
  }, [onListingSelect, t]);

  const syncMarkers = useCallback((map, listingsToShow) => {
    const PriceMarkerOverlay = createPriceOverlayClass(window.google);
    const nextIds = new Set(listingsToShow.map((listing) => listing.id));

    overlaysRef.current.forEach((overlay, listingId) => {
      if (!nextIds.has(listingId)) {
        overlay.setMap(null);
        overlaysRef.current.delete(listingId);
      }
    });

    listingsToShow.forEach((listing) => {
      if (overlaysRef.current.has(listing.id)) {
        return;
      }

      const position = new window.google.maps.LatLng(
        Number(listing.latitude),
        Number(listing.longitude)
      );

      const overlay = new PriceMarkerOverlay({
        position,
        label: formatPriceLabel(listing.price),
        listing,
        isRental: listing.type === 'rental',
        onSelect: (selected) => showListingInfo(map, selected),
      });
      overlay.setMap(map);
      overlaysRef.current.set(listing.id, overlay);
    });
  }, [showListingInfo]);

  const applySearchViewport = useCallback(async (map) => {
    const center = initialDestination
      ? await getCityCoordinates(initialDestination).catch(() => null)
      : null;

    const defaultCenter = { lat: 38.9072, lng: -77.0369 };
    const mapCenter = center
      ? { lat: center.lat, lng: center.lng }
      : mappableListings[0]
        ? { lat: Number(mappableListings[0].latitude), lng: Number(mappableListings[0].longitude) }
        : defaultCenter;

    const searchRadiusMiles = initialRadiusMiles || 20;
    const targetZoom = zoomForRadiusMiles(searchRadiusMiles);

    isApplyingSearchViewportRef.current = true;
    userInteractedRef.current = false;
    initialPositionDoneRef.current = false;
    removeCircle();

    map.setCenter(mapCenter);
    map.setZoom(targetZoom);

    circleRef.current = new window.google.maps.Circle({
      map,
      center: mapCenter,
      radius: searchRadiusMiles * 1609.34,
      fillColor: '#1976d2',
      fillOpacity: 0.08,
      strokeColor: '#1976d2',
      strokeOpacity: 0.35,
      strokeWeight: 1,
    });

    window.google.maps.event.trigger(map, 'resize');

    const initialBounds = boundsFromCenterAndRadius(mapCenter, searchRadiusMiles);
    emitViewport(initialBounds);
    initialPositionDoneRef.current = true;
    window.requestAnimationFrame(() => {
      isApplyingSearchViewportRef.current = false;
    });
  }, [initialDestination, initialRadiusMiles, mappableListings, emitViewport, removeCircle]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) {
      return undefined;
    }

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 38.9072, lng: -77.0369 },
      zoom: zoomForRadiusMiles(20),
      mapTypeId: 'roadmap',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      scaleControl: false,
      rotateControl: false,
      clickableIcons: false,
      gestureHandling: 'greedy',
      maxZoom: 15,
      styles: STAYS_MAP_STYLES,
    });

    mapInstanceRef.current = map;
    setMapInstanceReady(true);

    mapListenersRef.current = [
      map.addListener('idle', () => {
        if (!initialPositionDoneRef.current) {
          return;
        }
        if (userInteractedRef.current) {
          const bounds = boundsFromGoogleMap(map);
          if (bounds) {
            emitViewport(bounds);
          }
          removeCircle();

          // Debounced auto-search: fire one new search once the user stops
          // panning/zooming, instead of on every intermediate idle event.
          if (searchAreaTimerRef.current) {
            clearTimeout(searchAreaTimerRef.current);
          }
          searchAreaTimerRef.current = setTimeout(() => {
            searchAreaTimerRef.current = null;
            userInteractedRef.current = false;
            triggerSearchAreaRef.current?.();
          }, SEARCH_AREA_DEBOUNCE_MS);
        }
      }),
      map.addListener('dragstart', () => {
        userInteractedRef.current = true;
      }),
      map.addListener('zoom_changed', () => {
        if (initialPositionDoneRef.current && !isApplyingSearchViewportRef.current) {
          userInteractedRef.current = true;
        }
      }),
    ];

    const overlays = overlaysRef.current;

    return () => {
      if (searchAreaTimerRef.current) {
        clearTimeout(searchAreaTimerRef.current);
        searchAreaTimerRef.current = null;
      }
      clearMapListeners();
      overlays.forEach((overlay) => overlay.setMap(null));
      overlays.clear();
      removeCircle();
      mapInstanceRef.current = null;
      setMapInstanceReady(false);
      initialPositionDoneRef.current = false;
      lastSearchKeyRef.current = null;
    };
  }, [mapReady, clearMapListeners, emitViewport, removeCircle]);

  useEffect(() => {
    if (!mapInstanceReady || !mapInstanceRef.current || searchKey == null) {
      return undefined;
    }
    // Only reposition for a genuinely new search; provider results streaming in
    // re-create callbacks but must not snap the map back while the user explores.
    if (lastSearchKeyRef.current === searchKey) {
      return undefined;
    }
    lastSearchKeyRef.current = searchKey;

    let cancelled = false;

    const positionForSearch = async () => {
      if (cancelled || !mapInstanceRef.current) {
        return;
      }
      await applySearchViewport(mapInstanceRef.current);
    };

    positionForSearch().catch((error) => {
      console.warn('Stays map positioning failed:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [mapInstanceReady, searchKey, applySearchViewport]);

  useEffect(() => {
    if (!mapInstanceReady || !mapInstanceRef.current) {
      return;
    }
    syncMarkers(mapInstanceRef.current, mappableListings);
  }, [mapInstanceReady, mappableListings, syncMarkers]);

  const triggerSearchArea = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !onSearchArea) {
      return;
    }

    const center = map.getCenter();
    if (!center) {
      return;
    }

    const bounds = boundsFromGoogleMap(map);
    let radiusMiles = initialRadiusMiles || 20;
    if (bounds) {
      const latSpanMiles = milesBetween(bounds.south, center.lng(), bounds.north, center.lng());
      const lngSpanMiles = milesBetween(center.lat(), bounds.west, center.lat(), bounds.east);
      radiusMiles = Math.max(1, Math.min(100, Math.round(Math.min(latSpanMiles, lngSpanMiles) / 2)));
    }

    onSearchArea({ lat: center.lat(), lng: center.lng(), radiusMiles });
  }, [onSearchArea, initialRadiusMiles]);

  useEffect(() => {
    triggerSearchAreaRef.current = triggerSearchArea;
  }, [triggerSearchArea]);

  if (mapError) {
    return (
      <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
        <Typography color="text.secondary">{t('stays.mapUnavailable')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', mb: 3 }}>
      <Box
        ref={mapRef}
        sx={{
          height: MAP_HEIGHT,
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.100',
        }}
      />
      {(!mapReady || !mapInstanceReady) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.7)',
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      {mapReady && mapInstanceReady && mappableListings.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('stays.mapNoCoordinates')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export {
  formatPriceLabel,
  MAP_HEIGHT,
  milesBetween,
  zoomForRadiusMiles,
  boundsFromGoogleMap,
  boundsFromCenterAndRadius,
  isListingInMapBounds,
};
export default StaysMapView;
